import * as React from 'react';
import { Input } from 'antd';
import { InputProps } from 'antd/lib/input/Input';
import styled from 'styled-components';
import { path } from 'ramda';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { LiteralUnion } from 'antd/es/_util/type';

import InputKeywordsFetched, { InputKeywordsFetchedProps } from 'components/organisms/input-keywords';

interface PrimaryInputProps {
  width?: string | number;
  isEditable?: boolean;
  type?: LiteralUnion<
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
  >;
}

export interface CustomInputProps {
  upLabel?: string;
  subLabel?: string;
  leftLabel?: string | React.ReactNode;
  wrapperStyle?: React.CSSProperties;
}

const Wrapper = styled.div``;
const WrapperLabel = styled.div`
  display: flex;
`;

const LeftLabel = styled.label`
  display: flex;
  align-items: center;
  padding-right: 10px;
`;
const LabelWrapper = styled.div`
  width: max-content;
`;
const UpLabel = styled.label`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const WrapperInput = styled.div<PrimaryInputProps>`
  input {
    width: ${(props) => (props.width ? props.width : '100px')};
    border-radius: 5px;
    &:focus {
      ${(props) => !props.isEditable && 'border-color: #cccccc !important;'};
      ${(props) => !props.isEditable && 'box-shadow: none !important;'};
    }
    &:hover {
      ${(props) => !props.isEditable && 'border-color: #cccccc !important;'};
      ${(props) => !props.isEditable && 'box-shadow: none !important;'};
    }
  }
  width: ${(props) => (props.width ? props.width : '100px')};
`;

type Props = CustomInputProps &
  PrimaryInputProps &
  (({ withKeywords?: false } & InputProps) | ({ withKeywords: true } & InputKeywordsFetchedProps));

const StyledInput: React.FC<Props> = ({
  upLabel,
  subLabel,
  leftLabel,
  width,
  wrapperStyle,
  withKeywords,
  type,
  ...inputProps
}) => {
  return (
    <Wrapper style={wrapperStyle}>
      {upLabel && <UpLabel htmlFor={inputProps.name}>{upLabel}</UpLabel>}
      {subLabel && <span>{subLabel}</span>}
      <WrapperLabel>
        {leftLabel && (
          <LeftLabel htmlFor={inputProps.name}>
            <LabelWrapper>{leftLabel}</LabelWrapper>
          </LeftLabel>
        )}
        {type !== 'password' && (
          <WrapperInput width={width} isEditable={inputProps?.contentEditable !== false}>
            {withKeywords ? (
              <InputKeywordsFetched id={inputProps.name} {...(inputProps as InputKeywordsFetchedProps)} />
            ) : (
              <Input id={inputProps.name} type={type} {...(inputProps as InputProps)} />
            )}
          </WrapperInput>
        )}
        {type === 'password' && (
          <WrapperInput width={width} isEditable={inputProps?.contentEditable !== false}>
            <Input.Password
              id={inputProps.name}
              type={type}
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              {...(inputProps as InputProps)}
            />
          </WrapperInput>
        )}
      </WrapperLabel>
    </Wrapper>
  );
};

export default StyledInput;
